import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Home, Shield, Users, Globe, ArrowRight, CheckCircle2 } from "lucide-react";
import { waLink } from "@/lib/api";

const SERVICES = [
  {
    slug: "plot-monitoring",
    Icon: MapPin,
    title: "Plot Monitoring",
    who: "Owners of vacant plots, farm lands and investment lands.",
    what: ["Scheduled site visits", "Geo-tagged photos", "Boundary and fencing check", "Encroachment risk observation", "Debris / dumping alerts", "Annual condition summary"],
    receive: "WhatsApp / email reports + annual property condition summary.",
    pricing: "Starting ₹7,500 / year + GST",
    img: "https://images.pexels.com/photos/37822408/pexels-photo-37822408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "rental-management",
    Icon: Home,
    title: "Rental Management",
    who: "Landlords, NRIs and investors with rented homes or commercial space.",
    what: ["Tenant coordination", "Rent follow-up", "Rental agreement support", "Move-in inventory report", "Periodic inspection", "Maintenance ticket coordination", "Vendor estimate support", "Move-out inspection support"],
    receive: "Monthly statements, agreement reminders and inspection reports.",
    pricing: "Starting ₹1,250 / month + GST or % management fee",
    img: "https://images.pexels.com/photos/9870231/pexels-photo-9870231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "vacant-home",
    Icon: Shield,
    title: "Vacant Home Monitoring",
    who: "Owners with locked flats or houses kept for future use.",
    what: ["Periodic property inspection", "Doors, windows and visible damage check", "Seepage and leakage observation", "EB meter / common area observation", "Society / security interaction", "Photo and video updates", "Maintenance recommendation report"],
    receive: "Photo / video updates and a clear maintenance recommendation report.",
    pricing: "Starting ₹15,000 / year + GST",
    img: "https://images.pexels.com/photos/32355381/pexels-photo-32355381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "senior-citizen",
    Icon: Users,
    title: "Senior Citizen Property Care",
    who: "Owners who need trusted in-person assistance for property tasks.",
    what: ["Tenant call handling", "Repair coordination", "Utility & bill reminders", "Documentation support", "Property visits & verification", "Family update calls"],
    receive: "Calm, respectful local assistance with clear updates to you or your family.",
    pricing: "Bundled with Concierge plan or custom",
    img: "https://images.pexels.com/photos/19811308/pexels-photo-19811308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "nri-management",
    Icon: Globe,
    title: "NRI Property Management",
    who: "Owners living abroad needing a trusted Chennai representative.",
    what: ["Dedicated relationship manager", "Property tax / EB / society reminders", "Vendor & legal coordination", "Emergency visit support", "Quarterly property health report"],
    receive: "Calm, time-zone-aware communication with proof at every step.",
    pricing: "Custom pricing based on scope",
    img: "https://images.pexels.com/photos/7919624/pexels-photo-7919624.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      <section className="bg-brand-navy text-white">
        <div className="container-x py-20 md:py-28">
          <div className="eyebrow !text-brand-cyan">Services</div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Choose the right care for your property.
          </h1>
          <p className="mt-5 text-white/80 max-w-2xl">From vacant plot monitoring to complete NRI property management — pick what fits, or let us recommend the right combination after a free property review.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="grid gap-10">
          {SERVICES.map((s, i) => (
            <article key={s.slug} data-testid={`service-detail-${s.slug}`} className="card-flat grid lg:grid-cols-12 overflow-hidden">
              <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                <img src={s.img} alt={s.title} className="w-full h-full min-h-[260px] object-cover" />
              </div>
              <div className="lg:col-span-7 p-8 md:p-10">
                <div className="flex items-center gap-3">
                  <s.Icon size={24} className="text-brand-cyan" />
                  <div className="eyebrow">{s.title}</div>
                </div>
                <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-brand-navy">{s.who}</h2>
                <div className="mt-5 grid sm:grid-cols-2 gap-y-2 gap-x-6">
                  {s.what.map((w) => (
                    <div key={w} className="flex items-start gap-2 text-sm text-brand-slate/85">
                      <CheckCircle2 size={16} className="text-brand-cyan mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-sm text-brand-slate/80"><b className="text-brand-navy">You receive:</b> {s.receive}</p>
                <div className="mt-3 text-sm text-brand-slate/80"><b className="text-brand-navy">Pricing:</b> {s.pricing}</div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-cyan">Book a Property Review</Link>
                  <a
                    href={waLink(`Hi Hi-Tech Realty, I'd like to know more about ${s.title}.`)}
                    target="_blank" rel="noreferrer" className="btn-whatsapp"
                  >Ask on WhatsApp</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-brand-navy text-white">
        <div className="container-x py-16 grid md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold">Not sure which plan fits your property?</h2>
            <p className="mt-3 text-white/80">Tell us about your property — location, type and current concern. We'll recommend the right care plan.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="btn-cyan w-full">Help Me Choose <ArrowRight size={14} /></Link>
            <Link to="/pricing" className="btn-ghost-navy w-full !text-white !border-white">View Pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
